import {makeApiCall} from '../core';
import {
    Visit,
    Invoice as SharedInvoice,
    InvoiceLineItem as SharedInvoiceLineItem,
    VisitReviewReason,
    BulkSelection,
    PhotoTag,
} from '@ruta/shared';
import type {ItemEnvelope, ListEnvelope} from '@ruta/shared';
import {
    EnhancedSubscriptionResponse,
    WorkQueueSubscription,
    CrewCapacity,
    UpdateVisitRequest,
    VisitAssignmentUpdate,
    VisitDetailsUpdate,
    VisitScheduleUpdate,
    VisitExecutionUpdate,
} from '@ruta/shared/api';
import {
    UpdateVisitPositionRequest,
    UpdateVisitPositionResponse,
    DispatchedPendingVisit,
} from '@ruta/shared/types/api/visits';

// Legacy types still used in webapp (to be migrated)
import type {AssignedVisit, ReviewAction, UnscheduledVisit, VisitAssignmentData, VisitInReview,} from '../types';
import type { Customer, CustomerSubscription, DispatchRequest, DispatchResponse } from '@ruta/shared';

// =====================
// Visit Management API
// =====================

/**
 * Fetch a single visit with full details including photos and notes.
 * Use this when you need complete visit data (e.g., for detail views).
 */
export const getVisit = async (visitId: string): Promise<{
  visit: Visit;
  subscription?: CustomerSubscription;
  customer?: Customer;
  noteViewers: Array<{ userId: string; userName: string; readAt: string }>;
  priorityNoteViewers: Array<{ userId: string; userName: string; readAt: string }>;
}> => {
  const res = await makeApiCall<ItemEnvelope<{
    visit: Visit;
    subscription?: CustomerSubscription;
    customer?: Customer;
    noteViewers: Array<{ userId: string; userName: string; readAt: string }>;
    priorityNoteViewers: Array<{ userId: string; userName: string; readAt: string }>;
  }>>('GET', `/visits/${visitId}`);
  return res.data;
};

export const getUnscheduledVisits = async (): Promise<{
  unscheduledVisits: UnscheduledVisit[];
  count: number;
  summary: {
    total: number;
    approved: number;
    needsPricing: number;
    byPriority: {
      urgent: number;
      high: number;
      normal: number;
      low: number;
    };
  };
}> => {
  return makeApiCall('GET', '/visits/unscheduled');
};

export const assignVisitToCrew = async (
  visitId: string,
  assignmentData: Omit<VisitAssignmentData, 'unscheduledVisitId'>,
): Promise<{
  message: string;
  visit: AssignedVisit;
  crew: {
    id: string;
    name: string;
    type: string;
  };
}> => {
  return makeApiCall('POST', `/visits/${visitId}/assignments`, assignmentData);
};

// =====================
// Visit Review Queue API (RUT-61)
// =====================

export const getVisitsInReview = async (): Promise<{
  visitsInReview: VisitInReview[];
  count: number;
  summary: {
    total: number;
    pendingReview: number;
    billableExceptions: number;
    nonBillableExceptions: number;
    byPriority: {
      urgent: number;
      high: number;
      normal: number;
      low: number;
    };
  };
}> => {
  return makeApiCall('GET', '/visits/review-queue');
};

export const reviewVisit = async (
  visitId: string,
  reviewAction: ReviewAction,
): Promise<{
  visit: VisitInReview;
  message: string;
}> => {
  return makeApiCall('POST', `/visits/${visitId}/review`, reviewAction);
};


// =====================
// Visit Review API - completing a NEEDS_REVIEW visit. The queue itself is read
// with getVisits({ needsReview: true }).
// =====================

export interface CompleteReviewRequest {
  action: 'approved' | 'adjusted' | 'requires_follow_up';
  reviewNotes: string;
  adjustedAmount?: number;
  adjustmentReason?: string;
  notifyCustomer?: boolean;
  customerMessage?: string;
}

export const completeVisitReview = async (
  visitId: string,
  review: CompleteReviewRequest
): Promise<{ visit: Visit }> => {
  const res = await makeApiCall<ItemEnvelope<{ visit: Visit }>>(
    'POST',
    `/visits/${visitId}/review/complete`,
    review,
  );
  return res.data;
};

// =====================
// Customer Invoices API (for service history)
// =====================

interface InvoiceLineItem extends SharedInvoiceLineItem {
  id: string;
  quantity: number;
  unitPrice: number;
}

interface CustomerInvoice extends SharedInvoice {
  jobId: string;
  customerName?: string;
  lineItems: InvoiceLineItem[];
  jobType?: string;
  propertyAddress?: string;
  transactionId?: string;
  failureReason?: string;
  chargeType?: string;
  refundedAmount?: number;
}

export const getCustomerInvoices = async (
  customerId: string,
  limit?: number,
  startKey?: string,
): Promise<{
  invoices: CustomerInvoice[];
  count: number;
  hasMore: boolean;
  nextStartKey?: string;
  summary?: {
    totalInvoices: number;
    totalAmount: number;
    statusBreakdown: Record<string, number>;
  };
}> => {
  const params = new URLSearchParams();
  params.append('customerId', customerId);
  if (limit) params.append('limit', limit.toString());
  if (startKey) params.append('startKey', startKey);

  const queryString = params.toString();
  const res = await makeApiCall<ItemEnvelope<{
    invoices: CustomerInvoice[];
    count: number;
    hasMore: boolean;
    nextStartKey?: string;
    summary?: {
      totalInvoices: number;
      totalAmount: number;
      statusBreakdown: Record<string, number>;
    };
  }>>('GET', `/invoices?${queryString}`);
  return res.data;
};

// =====================
// Work Queue API
// =====================

// Re-export WorkQueueSubscription and CrewCapacity from shared
export type { WorkQueueSubscription, CrewCapacity };

export const getWorkQueueSubscriptions = async (
  serviceTemplateId?: string,
  crewId?: string,
  showUnassigned?: boolean,
  subscriptionStatus?: string,
  limit?: number,
  nextToken?: string,
  rateVisibility?: string,
  schedulingStatus?: string,
  sortOrder?: 'asc' | 'desc',
): Promise<{
  subscriptions: EnhancedSubscriptionResponse[];
  crewCapacities: CrewCapacity[];
  summary: {
    totalServices: number;
    totalMinutes: number;
    byService: Record<string, { count: number; minutes: number }>;
  };
  pagination: {
    hasMore: boolean;
    nextToken?: string;
    totalCount: number;
    pageSize: number;
  };
}> => {
  const params = new URLSearchParams();
  if (serviceTemplateId) params.append('serviceTemplateId', serviceTemplateId);
  if (crewId) params.append('crewId', crewId);
  if (showUnassigned) params.append('unassigned', 'true');
  if (subscriptionStatus) params.append('subscriptionStatus', subscriptionStatus);
  if (rateVisibility) params.append('rateVisibility', rateVisibility);
  if (schedulingStatus) params.append('schedulingStatus', schedulingStatus);
  if (sortOrder) params.append('sortOrder', sortOrder);
  if (limit) params.append('limit', limit.toString());
  if (nextToken) params.append('nextToken', nextToken);

  const queryString = params.toString();
  const res = await makeApiCall<{
    data: EnhancedSubscriptionResponse[];
    pagination: { nextCursor: string | null; hasMore: boolean };
  }>('GET', `/subscriptions?${queryString}`);
  return {
    subscriptions: res.data,
    crewCapacities: [],
    summary: { totalServices: 0, totalMinutes: 0, byService: {} },
    pagination: {
      hasMore: res.pagination.hasMore,
      nextToken: res.pagination.nextCursor ?? undefined,
      totalCount: res.data.length,
      pageSize: res.data.length,
    },
  };
};

export const updateSubscriptionSchedule = async (
  customerId: string,
  serviceTemplateId: string,
  data: {
    preferredDayOfWeek?: number;
    preferredDayOfMonth?: number;
    preferredMonth?: number;
    preferredDayOfYear?: number;
    assignedCrewId?: string;
    order?: number;
    rank?: string;
    action?: 'accept' | 'reject' | 'activate' | 'pause';
  },
): Promise<{
  subscription: any;
  message: string;
}> => {
  return makeApiCall('PATCH', `/customers/${customerId}/subscriptions/${serviceTemplateId}`, data);
};

// =====================
// Schedule Management API
// =====================

export interface VisitWithDetails {
  id: string;
  customerId: string;
  serviceTemplateId: string;
  status: string;
  adminNotes?: string;
  customer?: {
    id: string;
    name: string;
    address: string;
    phone?: string;
    email?: string;
  };
  serviceTemplate?: {
    id: string;
    name: string;
    frequency: string;
    estimatedDuration?: number;
  };
  assignedCrewId?: string;
  assignedCrewName?: string;
}

export type SortOption = 
  | 'smart-schedule'
  | 'route-optimized'
  | 'priority'
  | 'crew-assignment'
  | 'service-type'
  | 'date'
  | 'customer'
  | 'status'
  | 'duration';

export type SortDirection = 'asc' | 'desc';

/**
 * Query modes:
 *   crewId                              → crew queue (pending, by rank)
 *   crewId + completed + from + to      → crew history (terminal, by completion date)
 *   needsReview                         → all NEEDS_REVIEW visits
 *   dispatchJobId                       → visits for a dispatch job
 *   customerId                          → customer visits
 *   status=not_started                  → pending visits (cursor-paged)
 *   status=not_started&nextPerCrew=true → one next-up visit per crew (Live)
 *   crewId&status=not_started&dispatched=true → a crew's dispatched, not-started visits
 */
export type GetVisitsOptions = {
  crewId?: string;
  completed?: boolean;
  from?: string;
  to?: string;
  needsReview?: boolean;
  /** With crewId + status 'not_started': only that crew's dispatched visits. */
  dispatched?: boolean;
  dispatchJobId?: string;
  customerId?: string;
  status?: string;
  fields?: 'summary';
  /** Cursor from a previous page's pagination.nextCursor */
  cursor?: string | null;
  /** Page size (API clamps to 200 for org-wide pending) */
  limit?: number;
  /** One pending visit per crew — for Live next-up, not the full backlog */
  nextPerCrew?: boolean;
  /**
   * Request pagination.total on the first open-queue page. Runs a full GSI
   * COUNT walk — expensive; omit for list/map paging, use only for rollups.
   */
  includeTotal?: boolean;
};

export type GetVisitsResult = {
  visits: Visit[];
  count: number;
  /** Exact match count across all pages when the API can compute it (page 1). */
  total?: number;
  hasMore: boolean;
  nextCursor: string | null;
};

export const getVisits = async (opts: GetVisitsOptions = {}): Promise<GetVisitsResult> => {
  const params = new URLSearchParams();

  if (opts.needsReview) params.append('needsReview', 'true');
  if (opts.dispatched) params.append('dispatched', 'true');
  if (opts.crewId) params.append('crewId', opts.crewId);
  if (opts.completed) params.append('completed', 'true');
  if (opts.from) params.append('from', opts.from);
  if (opts.to) params.append('to', opts.to);
  if (opts.dispatchJobId) params.append('dispatchJobId', opts.dispatchJobId);
  if (opts.customerId) params.append('customerId', opts.customerId);
  if (opts.status) params.append('status', opts.status);
  if (opts.fields) params.append('fields', opts.fields);
  if (opts.cursor) params.append('cursor', opts.cursor);
  if (opts.limit != null) params.append('limit', String(opts.limit));
  if (opts.nextPerCrew) params.append('nextPerCrew', 'true');
  if (opts.includeTotal) params.append('includeTotal', 'true');

  const queryString = params.toString();
  const res = await makeApiCall<ListEnvelope<Visit>>('GET', `/visits?${queryString}`);
  return {
    visits: res.data,
    count: res.data.length,
    total: res.pagination.total,
    hasMore: res.pagination.hasMore,
    nextCursor: res.pagination.nextCursor,
  };
};

/**
 * Drain every page of a visits query. Prefer `getVisits` + infinite scroll when
 * the UI can page; use this only when a full set is required (reorder tools).
 * Does not request includeTotal — drain length is the total.
 */
export const getAllVisitPages = async (
  opts: Omit<GetVisitsOptions, 'cursor' | 'includeTotal'> = {},
): Promise<GetVisitsResult> => {
  const pageLimit = opts.limit ?? 200;
  const all: Visit[] = [];
  let cursor: string | null | undefined;
  let guard = 0;

  do {
    const page = await getVisits({ ...opts, limit: pageLimit, cursor: cursor ?? undefined });
    all.push(...page.visits);
    cursor = page.hasMore ? page.nextCursor : null;
    guard += 1;
    // Safety: 200 * 100 = 20k visits max for a single client drain
    if (guard > 100) break;
  } while (cursor);

  return {
    visits: all,
    count: all.length,
    total: all.length,
    hasMore: false,
    nextCursor: null,
  };
};

export const regenerateSchedule = async (dateRange: { from: string; to: string }): Promise<{
  generatedVisits: number;
  affectedSubscriptions: number;
  dateRange: {
    from: string;
    to: string;
  };
}> => {
  const res = await makeApiCall<ItemEnvelope<{
    generatedVisits: number;
    affectedSubscriptions: number;
    dateRange: { from: string; to: string };
  }>>('POST', '/visits', dateRange);
  return res.data;
};

export const createSingleVisit = async (data: {
  customerId: string;
  serviceTemplateId: string;
  crewId?: string;
  budgetedHours?: number;
  adminNotes?: string;
  // Local YYYY-MM-DD for a one-off scheduled visit (RUT-2137). Omit to add to the
  // crew's queue now.
  scheduledDate?: string;
  originalVisitId?: string;
  isBillable?: boolean;
  followupBillingMethod?: string;
  followupRate?: number;
  visitType?: string;
  // Override the same-day duplicate-visit guard (RUT-2470): set true to add a
  // visit even though one for this service already completed today.
  allowDuplicate?: boolean;
  followupRequirements?: {
    requireBeforePhotos?: boolean;
    requireAfterPhotos?: boolean;
    notifyAdminsOnCompletion?: boolean;
    notifyCustomerOnCompletion?: boolean;
    crewConfirmationMessageId?: string;
    crewConfirmationConversationId?: string;
  };
  // Photo-review follow-up (RUT-3578): copy these photos onto the new visit.
  attachedPhotos?: Array<{ visitId: string; photoId: string }>;
}): Promise<{
  visit: {
    id: string;
    customerId: string;
    customerName: string;
    serviceTemplateId: string;
    serviceName: string;
    assignedCrewId?: string;
    budgetedHours: number;
    isFollowupVisit?: boolean;
    originalVisitId?: string;
  };
}> => {
  const res = await makeApiCall<ItemEnvelope<{
    visit: {
      id: string;
      customerId: string;
      customerName: string;
      serviceTemplateId: string;
      serviceName: string;
      assignedCrewId?: string;
      budgetedHours: number;
      isFollowupVisit?: boolean;
      originalVisitId?: string;
    };
  }>>('POST', '/visits', {
    customerId: data.customerId,
    serviceTemplateId: data.serviceTemplateId,
    crewId: data.crewId,
    budgetedHours: data.budgetedHours,
    adminNotes: data.adminNotes,
    scheduledDate: data.scheduledDate,
    originalVisitId: data.originalVisitId,
    isBillable: data.isBillable,
    followupBillingMethod: data.followupBillingMethod,
    followupRate: data.followupRate,
    visitType: data.visitType,
    allowDuplicate: data.allowDuplicate,
    followupRequirements: data.followupRequirements,
    attachedPhotos: data.attachedPhotos,
  });
  return res.data;
};

export const updateVisitOrder = async (visitOrders: {
  visitId: string;
  order: number;
}[]): Promise<{
  updatedVisits: number;
  visitIds: string[];
  failedVisits?: number;
  failedVisitIds?: string[];
}> => {
  const res = await makeApiCall<ItemEnvelope<{
    updatedVisits: number;
    visitIds: string[];
    failedVisits?: number;
    failedVisitIds?: string[];
  }>>('PUT', '/visits/order', { visitOrders });
  return res.data;
};

/**
 * A crew's dispatched, not-yet-started visits — the work that can be renumbered
 * when its route is reordered.
 */
export const getDispatchedPendingVisits = async (
  crewId: string,
): Promise<DispatchedPendingVisit[]> => {
  const { visits } = await getVisits({ crewId, status: 'not_started', dispatched: true });
  return visits.map((v) => ({
    visitId: v.id,
    subscriptionId: v.subscriptionId ?? '',
    rank: v.rank,
  }));
};

/**
 * Push a crew's route order onto its active dispatch. `subscriptionIds` is the
 * route order (`${customerId}#${serviceTemplateId}` per stop). Only dispatched,
 * not-yet-started visits are renumbered.
 */
export const applyRouteOrderToDispatch = async (
  crewId: string,
  subscriptionIds: string[],
): Promise<{ updatedVisits: number; visitIds: string[] }> => {
  const res = await makeApiCall<ItemEnvelope<{ updatedVisits: number; visitIds: string[] }>>(
    'POST',
    '/visits/route-order',
    { crewId, subscriptionIds },
  );
  return res.data;
};

export const updateSingleVisitPosition = async (
  visitId: string,
  data: UpdateVisitPositionRequest
): Promise<Omit<UpdateVisitPositionResponse, 'message'>> => {
  const res = await makeApiCall<ItemEnvelope<Omit<UpdateVisitPositionResponse, 'message'>>>(
    'PATCH',
    `/visits/${visitId}/position`,
    data,
  );
  return res.data;
};

export const updateVisit = async (
  visitId: string,
  data: UpdateVisitRequest | VisitScheduleUpdate | VisitDetailsUpdate | VisitAssignmentUpdate | VisitExecutionUpdate
): Promise<{
  visit: any;
}> => {
  const res = await makeApiCall<ItemEnvelope<{ visit: any }>>(
    'PATCH',
    `/visits/${visitId}`,
    data,
  );
  return res.data;
};

export const updateVisitStatus = async (
  visitId: string,
  data: {
    status: 'completed' | 'skipped' | 'cancelled';
    startedAt?: string; // ISO timestamp for when visit started
    completedAt?: string; // ISO timestamp for when visit completed
    totalServiceTime?: number;
    serviceOptions?: Array<{
      optionId: string;
      quantity: number;
    }>;
    adminNotes?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
    skipReason?: {
      type: 'predefined' | 'custom';
      reason: string;
      customNote?: string;
    };
    cancellationReason?: string;
  }
): Promise<{
  visit: VisitWithDetails;
}> => {
  const res = await makeApiCall<ItemEnvelope<{ visit: VisitWithDetails }>>(
    'PATCH',
    `/visits/${visitId}`,
    data,
  );
  return res.data;
};

export const cancelVisit = async (
  visitId: string,
  cancellationReason?: string
): Promise<{
  visit: any;
}> => {
  const res = await makeApiCall<ItemEnvelope<{ visit: any }>>('PATCH', `/visits/${visitId}`, {
    status: 'cancelled',
    ...(cancellationReason && { cancellationReason }),
  });
  return res.data;
};

export const discardVisit = async (
  visitId: string,
  discardReason: string
): Promise<{
  visit: Visit;
  newVisit: Visit;
}> => {
  const res = await makeApiCall<ItemEnvelope<{ visit: Visit; newVisit: Visit }>>(
    'PATCH',
    `/visits/${visitId}`,
    {
      status: 'discarded',
      discardReason,
    },
  );
  return res.data;
};

export interface AddVisitPhotoRequest {
  photoId: string;
  url: string;
  timestamp: string;
  latitude?: number;
  longitude?: number;
  // True capture metadata read from the image's EXIF before upload (RUT-2790).
  capturedAt?: string;
  capturedLatitude?: number;
  capturedLongitude?: number;
  note?: string;
  note_es?: string;
}

export interface AddVisitPhotoResponse {
  photo: any;
  visit: {
    id: string;
    photoCount: number;
  };
}

export const addVisitPhoto = async (
  visitId: string,
  data: AddVisitPhotoRequest,
): Promise<AddVisitPhotoResponse> => {
  const res = await makeApiCall<ItemEnvelope<AddVisitPhotoResponse>>(
    'POST',
    `/visits/${visitId}/photos`,
    data,
  );
  return res.data;
};

export const toggleVisitPhotoSharing = async (
  visitId: string,
  photoId: string,
  sharedWithCustomer: boolean,
): Promise<{ photo: any }> => {
  const res = await makeApiCall<ItemEnvelope<{ photo: any }>>(
    'PATCH',
    `/visits/${visitId}/photos/${photoId}/sharing`,
    { sharedWithCustomer },
  );
  return res.data;
};

/** Replace the predefined tags on a single visit photo. `tags: []` clears them. */
export const setVisitPhotoTags = async (
  visitId: string,
  photoId: string,
  tags: PhotoTag[],
): Promise<{ photo: any }> => {
  const res = await makeApiCall<ItemEnvelope<{ photo: any }>>(
    'PATCH',
    `/visits/${visitId}/photos/${photoId}`,
    { tags },
  );
  return res.data;
};

export const setVisitPhotosSharing = async (
  visitId: string,
  sharedPhotoIds: string[],
): Promise<{ photos: any[] }> => {
  const res = await makeApiCall<ItemEnvelope<{ photos: any[] }>>(
    'PATCH',
    `/visits/${visitId}/photos/sharing`,
    { sharedPhotoIds },
  );
  return res.data;
};

export const deleteVisitPhoto = async (
  visitId: string,
  photoId: string,
): Promise<void> => {
  await makeApiCall<void>('DELETE', `/visits/${visitId}/photos/${photoId}`);
};

/**
 * Restore a soft-deleted visit photo (RUT-2714). Reuses the visit PATCH
 * endpoint — no dedicated route — moving the photo back from the visit's
 * recently-deleted holding area into its live photos with sharing intact.
 */
export const restoreVisitPhoto = async (
  visitId: string,
  photoId: string,
): Promise<void> => {
  await makeApiCall<ItemEnvelope<{ visit: any }>>(
    'PATCH',
    `/visits/${visitId}`,
    { restorePhotoId: photoId },
  );
};

// =====================
// Visit Refunds API
// =====================

export interface CreateRefundRequest {
  amount?: number;
  refundType: 'refund' | 'credit';
  reason: string;
  notes?: string;
  // When set, the refund is charged back to this subcontractor (booked as an
  // adjustment against the company, like a credit adjustment). Omitted → the
  // company absorbs the refund and the sub keeps their pay.
  subcontractorCompanyId?: string;
  subcontractorAttributedAmount?: number;
}

export interface CreateRefundResponse {
  refundId?: string;
  visitId: string;
  amount: number;
  refundType: 'refund' | 'credit';
  status: 'pending' | 'succeeded' | 'failed';
  wasVoided: boolean;
}

export const refundVisit = async (
  visitId: string,
  data: CreateRefundRequest
): Promise<CreateRefundResponse> => {
  const res = await makeApiCall<ItemEnvelope<CreateRefundResponse>>(
    'POST',
    `/visits/${visitId}/refunds`,
    data,
  );
  return res.data;
};

// =====================
// Bulk Visit Updates API
// =====================

export interface BulkVisitUpdate {
  id: string;
  /** Set the admin "review required" flag — reviewRequired must be true. */
  reviewMetadata?: {
    reviewRequired: true;
    reviewReason?: VisitReviewReason;
  };
  /** Resolve a visit parked at status NEEDS_REVIEW (transitions status back). */
  approveReview?: Record<string, never>;
  /** Clear the admin review flag (status untouched). */
  removeReviewFlag?: Record<string, never>;
  cancel?: {
    cancellationReason?: string;
  };
  /** Turn off the per-visit completion bell and clear its provenance (RUT-2933). */
  disarmCompletionReminder?: Record<string, never>;
}

export interface BulkUpdateResponse {
  totalRequested: number;
  totalUpdated: number;
  /** Visits whose state didn't match the operation (e.g., approve on a non-NEEDS_REVIEW visit). Not errors. */
  totalSkipped: number;
  totalFailed: number;
  errors?: Array<{ visitId: string; error: string }>;
}

export const bulkUpdateVisits = async (
  visits: BulkVisitUpdate[]
): Promise<BulkUpdateResponse> => {
  const res = await makeApiCall<ItemEnvelope<BulkUpdateResponse>>('PATCH', '/visits', { visits });
  return res.data;
};

/**
 * Convenience function to cancel multiple visits
 */
export const bulkCancelVisits = async (
  visitIds: string[],
  cancellationReason?: string,
): Promise<BulkUpdateResponse> => {
  return bulkUpdateVisits(
    visitIds.map(id => ({
      id,
      cancel: { cancellationReason },
    }))
  );
};

/**
 * Cancel visits using a BulkSelection (supports cross-page selection)
 */
export const bulkCancelVisitsWithSelection = async (
  selection: BulkSelection,
  cancellationReason?: string,
): Promise<BulkUpdateResponse> => {
  const res = await makeApiCall<ItemEnvelope<BulkUpdateResponse>>('PATCH', '/visits', {
    selection,
    operation: { cancel: { cancellationReason } },
  });
  return res.data;
};

/**
 * Flag visits for review using a BulkSelection (supports cross-page selection)
 */
export const bulkFlagVisitsForReviewWithSelection = async (
  selection: BulkSelection,
  reason: VisitReviewReason = VisitReviewReason.MANUAL_FLAG,
  reviewNotes?: string,
): Promise<BulkUpdateResponse> => {
  const res = await makeApiCall<ItemEnvelope<BulkUpdateResponse>>('PATCH', '/visits', {
    selection,
    operation: {
      reviewMetadata: {
        reviewRequired: true,
        reviewReason: reason,
        ...(reviewNotes ? { reviewNotes } : {}),
      },
    },
  });
  return res.data;
};

/**
 * Approve review on visits parked at status NEEDS_REVIEW: transitions status back
 * to preReviewStatus and clears the flag. Visits not at NEEDS_REVIEW are silently
 * skipped (counted in totalSkipped, not totalFailed).
 */
export const bulkApproveReviewWithSelection = async (
  selection: BulkSelection,
): Promise<BulkUpdateResponse> => {
  const res = await makeApiCall<ItemEnvelope<BulkUpdateResponse>>('PATCH', '/visits', {
    selection,
    operation: { approveReview: {} },
  });
  return res.data;
};

/**
 * Turn off the per-visit completion bell on visits (RUT-2933), so an operator can
 * disarm from the list without opening each visit. Visits that aren't armed are
 * silently skipped (counted in totalSkipped, not totalFailed). Disarm-only —
 * arming stamps who/when from the session and stays on the single-visit route.
 */
export const bulkDisarmCompletionReminderWithSelection = async (
  selection: BulkSelection,
): Promise<BulkUpdateResponse> => {
  const res = await makeApiCall<ItemEnvelope<BulkUpdateResponse>>('PATCH', '/visits', {
    selection,
    operation: { disarmCompletionReminder: {} },
  });
  return res.data;
};

/**
 * Remove the admin "review required" flag on visits. Status is left alone.
 * Visits not currently flagged are silently skipped (counted in totalSkipped,
 * not totalFailed).
 */
export const bulkRemoveReviewFlagWithSelection = async (
  selection: BulkSelection,
): Promise<BulkUpdateResponse> => {
  const res = await makeApiCall<ItemEnvelope<BulkUpdateResponse>>('PATCH', '/visits', {
    selection,
    operation: { removeReviewFlag: {} },
  });
  return res.data;
};

// =====================
// Async Dispatch API
// =====================

// Dispatch request/response are the shared wire types — one home, no drift.
export type { DispatchRequest, DispatchResponse } from '@ruta/shared';

/**
 * Dispatch job status
 */
export type DispatchJobStatus = 'queued' | 'processing' | 'completed' | 'partial_failure' | 'failed' | 'cancelled';

/**
 * The dispatch dialog's message fields, as its consumers receive them.
 */
export interface DispatchDialogMessageParams {
  notifyCrews?: boolean;
  messageContent?: string;
  sendSms?: boolean;
  customerMessage?: { enabled: boolean; emailContent: string; smsContent: string };
}

/**
 * Map the dispatch dialog's message fields onto the dispatch request.
 */
export const toDispatchMessageFields = (
  params: DispatchDialogMessageParams,
): Pick<DispatchRequest, 'crewMessage' | 'crewMessageSms' | 'customerMessage'> => ({
  crewMessage: params.notifyCrews ? params.messageContent : undefined,
  crewMessageSms: params.notifyCrews ? params.sendSms : undefined,
  customerMessage: params.customerMessage?.enabled
    ? {
        emailContent: params.customerMessage.emailContent || undefined,
        smsContent: params.customerMessage.smsContent || undefined,
      }
    : undefined,
});

/**
 * What a dispatch actually sent (for the success toast) versus what was
 * requested but did not go out (for a warning toast). The response is the
 * source of truth, never the checkboxes.
 */
export const summarizeDispatchSends = (
  params: DispatchDialogMessageParams,
  response: DispatchResponse,
): { sent: string[]; failed: string[] } => {
  const sent: string[] = [];
  const failed: string[] = [];
  if (response.crewMessagesSent) {
    sent.push(`crew message sent to ${response.crewMessagesSent} crew${response.crewMessagesSent > 1 ? 's' : ''}`);
  } else if (params.notifyCrews && params.messageContent?.trim()) {
    failed.push('The crew message could not be sent');
  }
  if (response.customerCampaignId) {
    sent.push('customers messaged');
  } else if (
    params.customerMessage?.enabled &&
    (params.customerMessage.emailContent.trim() || params.customerMessage.smsContent.trim())
  ) {
    failed.push('The customer message could not be sent');
  }
  return { sent, failed };
};

/**
 * Full dispatch job details for status polling
 */
export interface DispatchJob {
  jobId: string;
  orgId: string;
  name: string;
  status: DispatchJobStatus;
  serviceTemplateIds?: string[];
  crewIds?: string[];
  totalSubscriptions: number;
  totalBatches: number;
  completedBatches: number;
  createdCount: number;
  completedVisitCount: number;
  cancelledVisitCount: number;
  skippedCount: number;
  failedCount: number;
  triggeredBy: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

/**
 * Dispatch visits asynchronously for subscriptions matching filters.
 * Creates a background job that processes subscriptions in batches.
 * Use getDispatchJob to poll for progress or subscribe to WebSocket updates.
 */
export const dispatchVisits = async (
  request: DispatchRequest
): Promise<DispatchResponse> => {
  const res = await makeApiCall<ItemEnvelope<DispatchResponse>>('POST', '/visits', {
    dispatch: request,
  });
  return res.data;
};

/**
 * Get dispatch job status, progress, and errors
 */
export const getDispatchJob = async (jobId: string): Promise<{ job: DispatchJob & { errors?: DispatchError[] } }> => {
  const response = await makeApiCall<ItemEnvelope<DispatchJob & { errors?: DispatchError[] }>>(
    'GET',
    `/dispatch-jobs/${jobId}`,
  );
  return { job: response.data };
};

/**
 * Structured dispatch error as returned by the API
 */
export interface DispatchError {
  customerId: string;
  serviceTemplateId: string;
  customerName: string;
  serviceName: string;
  reason: string;
  type: 'skipped' | 'failed';
}

/**
 * Dispatch job as returned by the list endpoint (uses jobId from domain model)
 */
export interface DispatchJobListItem {
  jobId: string;
  orgId: string;
  name?: string;
  status: DispatchJobStatus;
  serviceTemplateIds?: string[];
  crewIds?: string[];
  serviceTemplateNames?: string[];
  crewNames?: string[];
  totalSubscriptions: number;
  totalBatches: number;
  completedBatches: number;
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  rescheduledCount: number;
  completedVisitCount: number;
  cancelledVisitCount: number;
  visitCountMultiplier?: number;
  triggeredBy: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  progress: number;
}

/**
 * List dispatch jobs for the organization.
 * Pass `status: 'active'` to get every job still working visits (newest first,
 * exhaustive) for the live-ops dashboard; omit it for the paginated full history.
 */
export const listDispatchJobs = async (params?: {
  limit?: number;
  nextToken?: string;
  status?: 'active';
}): Promise<{
  jobs: DispatchJobListItem[];
  nextToken?: string;
  hasMore: boolean;
}> => {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken);
  if (params?.status) queryParams.append('status', params.status);
  const qs = queryParams.toString();
  const response = await makeApiCall<ListEnvelope<DispatchJobListItem>>(
    'GET',
    `/dispatch-jobs${qs ? `?${qs}` : ''}`,
  );
  return {
    jobs: response.data,
    nextToken: response.pagination.nextCursor ?? undefined,
    hasMore: response.pagination.hasMore,
  };
};

/**
 * Cancel a dispatch job (only if queued or processing).
 * Returns 204 No Content on success.
 */
export const cancelDispatchJob = async (jobId: string): Promise<void> => {
  await makeApiCall('DELETE', `/dispatch-jobs/${jobId}`);
};

/**
 * Get all visits for a dispatch job (bypasses date/week requirement)
 */
export const getVisitsByDispatchJob = async (dispatchJobId: string, opts?: { fields?: 'summary' }): Promise<{
  visits: Visit[];
  total: number;
  count: number;
  hasMore: boolean;
}> => {
  const params = new URLSearchParams({ dispatchJobId });
  if (opts?.fields) params.append('fields', opts.fields);
  const res = await makeApiCall<ListEnvelope<Visit>>('GET', `/visits?${params.toString()}`);
  return {
    visits: res.data,
    total: res.data.length,
    count: res.data.length,
    hasMore: res.pagination.hasMore,
  };
};

/**
 * Update dispatch job name
 */
export const updateDispatchJobName = async (jobId: string, name: string): Promise<DispatchJobListItem> => {
  const response = await makeApiCall<ItemEnvelope<DispatchJobListItem>>(
    'PATCH',
    `/dispatch-jobs/${jobId}`,
    { name },
  );
  return response.data;
};

/**
 * Update dispatch job visit count multiplier
 */
export const updateDispatchJobMultiplier = async (jobId: string, visitCountMultiplier: number): Promise<DispatchJobListItem> => {
  const response = await makeApiCall<ItemEnvelope<DispatchJobListItem>>(
    'PATCH',
    `/dispatch-jobs/${jobId}`,
    { visitCountMultiplier },
  );
  return response.data;
};

/**
 * Add subscriptions to an existing dispatch job (creates visits)
 */
export const addSubscriptionsToDispatchJob = async (
  jobId: string,
  subscriptions: Array<{ customerId: string; serviceTemplateId: string; scheduleId?: string }>,
): Promise<{
  job: DispatchJobListItem;
  createdCount: number;
  errors?: Array<{ customerId: string; serviceTemplateId: string; error: string }>;
}> => {
  const response = await makeApiCall<ItemEnvelope<{
    job: DispatchJobListItem;
    createdCount: number;
    errors?: Array<{ customerId: string; serviceTemplateId: string; error: string }>;
  }>>('PATCH', `/dispatch-jobs/${jobId}`, { addSubscriptions: subscriptions });
  return response.data;
};

/**
 * Clear (cancel) all non-terminal visits for a dispatch job
 */
export const clearDispatchJobVisits = async (jobId: string): Promise<DispatchJobListItem> => {
  const response = await makeApiCall<ItemEnvelope<DispatchJobListItem>>(
    'PATCH',
    `/dispatch-jobs/${jobId}`,
    { clearVisits: true },
  );
  return response.data;
};


